import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { USER_TYPE } from '../enums/user';
import { EncryptionService } from '../services/encryption.service';
import { StorageService } from '../services/storage.service';
import { Constants } from '../shared/constants';
import { environment } from '../../../environments/environment';

export const RoleGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const encryptionService = inject(EncryptionService);
  const storageService = inject(StorageService);
  const ENCRYPTION_KEY = `${environment.app}-${environment.appId}`;

  // Get expected roles from route data
  const expectedRoles = (route.data['roles'] as string[]).map((r) => r.toUpperCase());

  // Where to send an authenticated user whose role doesn't match the route
  const roleHomeRoutes: Record<string, string> = {
    [USER_TYPE.CLIENTS]: '/clients',
    [USER_TYPE.TAX_PREPARER]: '/tax-managers/dashboard',
    [USER_TYPE.ACCOUNTANT]: '/tax-managers/dashboard/accountant',
    [USER_TYPE.TENANT_ADMIN]: '/tax-managers/dashboard/supervisor',
    [USER_TYPE.SUPER_ADMIN]: '/admin-portal',
  };

  if (!authService.isAuthenticated()) {
    authService.goToAuth(router.routerState.snapshot.url);
    return false;
  }

  try {
    // Get the encrypted user object from storage
    const userEncrypted = storageService.get(Constants.STORAGE_VARIABLES.USER);

    if (!userEncrypted) {
      console.warn('No user data found in storage');
      router.navigate(['/auth/sign-in']);
      return false;
    }

    // Decrypt the user data
    const userDataString = encryptionService.decrypt(
      ENCRYPTION_KEY,
      userEncrypted,
      Constants.STORAGE_VARIABLES.USER,
    );

    // Parse the user object
    let user: { role?: string } | null = null;
    try {
      user = JSON.parse(userDataString);
    } catch {
      router.navigate(['/auth/sign-in']);
      return false;
    }

    // Extract the role from the user object
    const role = user?.role?.toUpperCase();

    if (!role || !expectedRoles.includes(role)) {
      // Role not authorized
      console.warn('Access denied: User role', role, 'not in expected roles', expectedRoles);

      // Redirect to the user's own home route instead of the denied one,
      // otherwise a mismatched role can bounce back into the same guard.
      router.navigate([roleHomeRoutes[role ?? ''] ?? '/auth/sign-in']);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in RoleGuard:', error);
    router.navigate(['/auth/sign-in']);
    return false;
  }
};
